/**
 * MS-08 rework 2026-06-07 · `.kanban-pull` интерактивная мини-доска WIP-лимитов.
 *
 * Mechanic: клик по карточке тянет её в следующую колонку. Если у колонки
 * назначения WIP-лимит заполнен — ход блокируется, pill мигает `.over`,
 * подсказка объясняет «заканчивай, потом начинай». Все карточки в Done →
 * финальный вывод. Студент чувствует pull-систему руками.
 *
 * Дельта MS-08 (item 5 · 2026-06-07): + кнопка «Сбросить» (`.kp-reset`,
 * программный reset без reload) + подсветка следующего хода (`.kp-task.active`
 * — самый правый ход, учит «заканчивай справа») + flash `.kp-task.moved`.
 *
 * Markup contract (per sprint-01.ts MS-08):
 *   <div class='kp-bar'><span class='kp-task-hint'>...</span>
 *        <button class='kp-reset'>Сбросить</button>
 *        <span class='kp-count'>Done: <span class='kp-done'>0</span> / N</span></div>
 *   <div class='kanban-demo kanban-pull' data-kanban-pull='<id>'>
 *     <div class='kdemo-col' data-limit=''>          ← '' или отсутствие = без лимита
 *       <div class='kdemo-head'><span>TO DO</span><span class='wip-pill kp-pill'>—</span></div>
 *       <div class='kp-cards'><div class='kdemo-card kp-task'>...</div>×N</div>
 *     </div>
 *     ×4 (последняя колонка = Done)
 *   </div>
 *   <div class='kp-hint' aria-live='polite'></div>
 *   <div class='kp-final'>...вывод...</div>
 *
 * kp-bar / kp-hint / kp-final / kp-reset ищутся в родителе .kanban-pull (siblings).
 * Progressive degradation: без JS — статичная доска, все карточки в первой
 * колонке, hint/final скрыты CSS-ом (как .qa-sorter).
 *
 * State: session-only (re-attempt on revisit · как mythCards/sorter).
 */
export function initKanbanPull(root: HTMLElement): () => void {
  const boards = Array.from(root.querySelectorAll<HTMLElement>('.kanban-pull'));
  const cleanups: Array<() => void> = [];

  for (const board of boards) {
    if (board.dataset.kpInit === '1') continue;
    board.dataset.kpInit = '1';

    const cols = Array.from(board.querySelectorAll<HTMLElement>('.kdemo-col'));
    const parent = board.parentElement;
    const hint = parent?.querySelector<HTMLElement>('.kp-hint') ?? null;
    const finalEl = parent?.querySelector<HTMLElement>('.kp-final') ?? null;
    const doneEl = parent?.querySelector<HTMLElement>('.kp-done') ?? null;
    const resetBtn = parent?.querySelector<HTMLButtonElement>('.kp-reset') ?? null;
    if (cols.length < 2) continue;

    const firstCol = cols[0];
    const firstBox = firstCol ? firstCol.querySelector<HTMLElement>('.kp-cards') : null;
    // initial card order (all start in col 0) — programmatic reset (no reload).
    const initialCards = Array.from(board.querySelectorAll<HTMLElement>('.kp-task'));
    const total = initialCards.length;
    let hintTimer: ReturnType<typeof setTimeout> | undefined;
    let moveTimer: ReturnType<typeof setTimeout> | undefined;

    const limitOf = (col: HTMLElement): number => {
      const raw = col.getAttribute('data-limit') ?? '';
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n : Infinity;
    };
    const cardsBox = (col: HTMLElement) => col.querySelector<HTMLElement>('.kp-cards');
    const countIn = (col: HTMLElement) => col.querySelectorAll('.kp-task').length;
    const hasRoom = (col: HTMLElement) => countIn(col) < limitOf(col);

    const syncPills = () => {
      cols.forEach((col) => {
        const pill = col.querySelector<HTMLElement>('.kp-pill');
        if (!pill) return;
        const lim = limitOf(col);
        if (lim === Infinity) {
          pill.textContent = '∞';
          pill.classList.remove('over');
        } else {
          const n = countIn(col);
          pill.textContent = `${n}/${lim}`;
          pill.classList.toggle('over', n >= lim);
        }
      });
      const lastCol = cols[cols.length - 1];
      if (doneEl && lastCol) doneEl.textContent = String(countIn(lastCol));
    };

    // Highlight the next recommended pull move: rightmost non-Done column that
    // holds a card whose destination has room. Teaches «finish from the right».
    const highlight = () => {
      board.querySelectorAll('.kp-task.active').forEach((t) => t.classList.remove('active'));
      if (board.classList.contains('done')) return;
      for (let i = cols.length - 2; i >= 0; i -= 1) {
        const col = cols[i];
        const next = cols[i + 1];
        if (!col || !next) continue;
        const card = col.querySelector<HTMLElement>('.kp-task');
        if (card && hasRoom(next)) {
          card.classList.add('active');
          return;
        }
      }
    };

    const showHint = (text: string) => {
      if (!hint) return;
      hint.textContent = text;
      hint.classList.add('visible');
      if (hintTimer) clearTimeout(hintTimer);
      hintTimer = setTimeout(() => hint.classList.remove('visible'), 3500);
    };

    const refresh = () => {
      syncPills();
      highlight();
    };

    const onClick = (e: Event) => {
      const task = (e.target as HTMLElement).closest<HTMLElement>('.kp-task');
      if (!task || !board.contains(task)) return;
      const col = task.closest<HTMLElement>('.kdemo-col');
      if (!col) return;
      const idx = cols.indexOf(col);
      if (idx < 0 || idx >= cols.length - 1) return;
      const dest = cols[idx + 1];
      if (!dest) return;
      const destBox = cardsBox(dest);
      if (!destBox) return;
      const isLastMove = idx + 1 === cols.length - 1;
      if (!isLastMove && !hasRoom(dest)) {
        const name = dest.querySelector('.kdemo-head span')?.textContent ?? 'колонке';
        showHint(
          `Лимит! В «${name}» уже ${countIn(dest)}/${limitOf(dest)}. Сначала протолкни задачу оттуда дальше — заканчивай, потом начинай.`,
        );
        return;
      }
      destBox.appendChild(task);
      task.classList.add('moved');
      if (moveTimer) clearTimeout(moveTimer);
      moveTimer = setTimeout(() => task.classList.remove('moved'), 420);
      refresh();
      const lastDone = cols[cols.length - 1];
      if (lastDone && countIn(lastDone) === total && finalEl) {
        finalEl.classList.add('visible');
        board.classList.add('done');
        board.querySelectorAll('.kp-task.active').forEach((t) => t.classList.remove('active'));
      }
    };

    // Reset · programmatic (mockup used location.reload — здесь пересоздаём
    // состояние, чтобы не терять прогресс остального тикета).
    const reset = () => {
      if (!firstBox) return;
      initialCards.forEach((c) => {
        c.classList.remove('moved', 'active');
        firstBox.appendChild(c);
      });
      board.classList.remove('done');
      finalEl?.classList.remove('visible');
      hint?.classList.remove('visible');
      if (hint) hint.textContent = '';
      refresh();
    };

    board.addEventListener('click', onClick);
    resetBtn?.addEventListener('click', reset);
    board.classList.add('kp-ready');
    parent?.classList.add('kp-on');
    refresh();

    cleanups.push(() => {
      board.removeEventListener('click', onClick);
      resetBtn?.removeEventListener('click', reset);
      if (hintTimer) clearTimeout(hintTimer);
      if (moveTimer) clearTimeout(moveTimer);
      delete board.dataset.kpInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
